import type { FC, PropsWithChildren } from "react";

import { type FormkitComponent, ComponentType } from "@hightouch/formkit";
import {
  Row,
  Switch,
  Text,
  Tooltip,
  Column,
  ChakraUnorderedList,
  ChakraListItem,
  Definition,
} from "@hightouch/htui";
import { useFormContext, Controller } from "react-hook-form";
import { useFlags } from "launchdarkly-react-client-sdk";
import pluralize from "pluralize";
import { isPresent } from "ts-extras";

import { useFormkitContext } from "src/formkit/components/formkit-context";

import { useSyncTemplateLockingRules } from "./use-sync-template-locking-rules";
import { OVERRIDE_CONFIG_FORM_KEY } from "./constants";

type SyncTemplateLockProps = {
  nodes: FormkitComponent[];
};

export const SyncTemplateLock: FC<PropsWithChildren<SyncTemplateLockProps>> = ({
  nodes,
}) => {
  const { showLockUI, relatedSyncsWithOverrides } = useFormkitContext();
  const { control, getValues } = useFormContext();
  const { journeysSyncTemplates } = useFlags();

  // Start of Selection
  let numberOfSyncLevelOverrides = 0;
  for (const node of nodes) {
    numberOfSyncLevelOverrides += relatedSyncsWithOverrides?.[node.key] ?? 0;
  }

  const { isLocked, message: disabledMessage } = useSyncTemplateLockingRules(
    nodes,
    numberOfSyncLevelOverrides,
    { allowAnyFieldLock: journeysSyncTemplates },
  );

  if (!showLockUI || nodes.length === 0) {
    return null;
  }

  return (
    <Row
      bg="base.background"
      px={4}
      py={1.5}
      height="min-content"
      borderRadius="md"
      sx={{
        label: {
          display: "flex",
          gap: 2,
          alignItems: "center",
          whiteSpace: "nowrap",
        },
      }}
    >
      <Column gap={2} justifyContent="center">
        <Text as="label" fontWeight="medium">
          <Definition message="Allow collaborators to edit this field when using this sync template">
            Editable
          </Definition>
          <Controller
            name={OVERRIDE_CONFIG_FORM_KEY}
            control={control}
            render={({ field }) => (
              <Tooltip
                isDisabled={!disabledMessage}
                placement="top-end"
                message={
                  typeof disabledMessage === "string" ? (
                    disabledMessage
                  ) : isPresent(disabledMessage) ? (
                    <Column
                      gap={2}
                      sx={{ "> span": { textAlign: "left", pl: 2 } }}
                    >
                      <Text color="white">{disabledMessage.label}</Text>
                      {disabledMessage &&
                        typeof disabledMessage === "object" && (
                          <ChakraUnorderedList>
                            {disabledMessage?.children.map((child) => (
                              <ChakraListItem
                                key={child}
                                sx={{ textAlign: "left" }}
                              >
                                <Text color="white">&quot;{child}&quot;</Text>
                              </ChakraListItem>
                            ))}
                          </ChakraUnorderedList>
                        )}
                    </Column>
                  ) : (
                    ""
                  )
                }
              >
                <Switch
                  aria-label="Enable overrides."
                  size="sm"
                  isDisabled={Boolean(disabledMessage)}
                  isChecked={!isLocked}
                  onChange={(value) => {
                    const currentOverrides = getValues(
                      OVERRIDE_CONFIG_FORM_KEY,
                    );
                    const newOverrides = { ...currentOverrides };

                    // Check if any nodes are journey settings fields
                    const hasJourneySettingsNodes = nodes.some(
                      (node) =>
                        node.key.includes("journey_settings.mode") ||
                        node.key.includes("journey_settings.exit_config") ||
                        node.component === ComponentType.JourneySettings,
                    );
                    if (hasJourneySettingsNodes) {
                      // For journey settings, set override at journey_settings level
                      newOverrides.journey_settings = {
                        overridable: value,
                      };
                    } else {
                      // For regular fields, set override at individual field level
                      nodes.forEach((node) => {
                        newOverrides[node.key] = {
                          overridable: value,
                        };
                      });
                    }

                    field.onChange(newOverrides);
                  }}
                />
              </Tooltip>
            )}
          />
        </Text>
        {numberOfSyncLevelOverrides > 0 && (
          <Text color="warning.base" size="sm">
            {numberOfSyncLevelOverrides} sync-level{" "}
            {pluralize("override", numberOfSyncLevelOverrides)}
          </Text>
        )}
      </Column>
    </Row>
  );
};
